import { isoWeekStart, localDateString } from './pactAutoCheckIn';

/**
 * Pact schedule config (②e locked §0.12): the cadence rides the existing
 * `cadence` column ('daily' | 'weekly' | 'custom'); specific-days schedules
 * ride the previously-unused `custom_schedule` jsonb in the HABIT-schedule
 * shape (interval + days, 0=Sun … 6=Sat — HabitSchedule.days convention).
 * Check-ins stay BINARY per day in v2 — this config only describes WHICH
 * days count as the commitment.
 */

export interface PactCustomSchedule {
  v: 1;
  interval: 'weekly';
  /** Days of week, 0=Sun … 6=Sat, sorted unique. */
  days: number[];
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Build the jsonb payload from picked days; null when nothing usable. */
export function buildCustomSchedule(days: number[]): PactCustomSchedule | null {
  const clean = [...new Set(days)].filter(d => Number.isInteger(d) && d >= 0 && d <= 6).sort();
  if (clean.length === 0) return null;
  return { v: 1, interval: 'weekly', days: clean };
}

/** Parse the raw jsonb column; tolerant of junk/legacy/missing values. */
export function parseCustomSchedule(raw: unknown): PactCustomSchedule | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as { v?: unknown; days?: unknown };
  if (obj.v !== 1 || !Array.isArray(obj.days)) return null;
  return buildCustomSchedule(obj.days.filter((d): d is number => typeof d === 'number'));
}

/**
 * Whether a side has checked in during the CURRENT cadence period:
 * daily/custom = today (local); weekly = since Monday of this ISO week.
 * `checkInDates` are yyyy-mm-dd strings (check_in_date column values).
 */
export function checkedInThisPeriod(cadence: string, checkInDates: string[], now: Date): boolean {
  if (cadence === 'weekly') {
    const weekStart = localDateString(isoWeekStart(now));
    return checkInDates.some(d => d >= weekStart);
  }
  return checkInDates.includes(localDateString(now));
}

/** Whether `date` is a commitment day (custom cadences skip off-days). */
export function isScheduledDay(
  cadence: string,
  customSchedule: PactCustomSchedule | null | undefined,
  date: Date
): boolean {
  if (cadence !== 'custom' || !customSchedule?.days?.length) return true;
  return customSchedule.days.includes(date.getDay());
}

/**
 * 4-state habit-calendar idiom over pact check-ins (②e P4): what one DAY
 * means for one SIDE. 'out' = outside the pact window; 'off' = not a
 * commitment day (custom off-days, and weekly cadences where the week —
 * not the day — is the unit, so unchecked days carry no judgment);
 * 'missed' = past commitment day without a check-in; 'upcoming' = today
 * or future commitment day.
 */
export type PactDayState = 'done' | 'missed' | 'upcoming' | 'off' | 'out';

export function pactDayState(
  date: Date,
  pact: {
    cadence: string;
    customSchedule?: PactCustomSchedule | null;
    startsAt?: string;
    endsAt?: string;
  },
  checkInDates: string[],
  now: Date
): PactDayState {
  const ds = localDateString(date);
  if (pact.startsAt && ds < localDateString(new Date(pact.startsAt))) return 'out';
  if (pact.endsAt && ds > localDateString(new Date(pact.endsAt))) return 'out';
  if (checkInDates.includes(ds)) return 'done';
  if (!isScheduledDay(pact.cadence, pact.customSchedule, date)) return 'off';
  if (pact.cadence === 'weekly') return 'off';
  return ds < localDateString(now) ? 'missed' : 'upcoming';
}

/** Human one-liner: 'Every day' / 'Once a week' / 'Mon, Wed & Fri'. */
export function describePactSchedule(
  cadence: string,
  customSchedule?: PactCustomSchedule | null
): string {
  if (cadence === 'daily') return 'Every day';
  if (cadence === 'weekly') return 'Once a week';
  const days = customSchedule?.days ?? [];
  if (days.length === 0) return 'Custom schedule';
  if (days.length === 7) return 'Every day';
  const labels = days.map(d => WEEKDAY_LABELS[d]);
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}
